import { Star } from "lucide-react";
import type { components } from "@shared-core/data/api-schema.generated";
import { resolvedItemFromSchema } from "@shared-core/data/api/catalogue";
import type { CatalogueField } from "@shared-core/data/catalogue-types";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body path the widget owns: the item's narrowing of its options and quantities, as the request sends it. */
const PATH = "narrowing";
/** The fields of its fixed options the item highlights on its product page (change 10). */
const HIGHLIGHTS = "highlights";

type ResolvedItemSchema = components["schemas"]["ResolvedCatalogueItemSchema"];

/** One field's narrowing as the request carries it: the options still offered (empty: all) and the one it starts on. */
interface FieldNarrowingWire {
  allowed_options?: string[];
  default?: string | null;
}

interface NarrowingWire {
  fields?: Record<string, FieldNarrowingWire>;
  quantities?: number[];
}

/** A field the item can narrow: a choice of more than one option in its parent. */
const isNarrowable = (f: CatalogueField) =>
  f.mode !== "fixed" &&
  f.mode !== "always_custom" &&
  (f.options?.length ?? 0) > 1;

/** The options a field still offers: its allowed subset, or every option. */
function offeredOf(
  field: CatalogueField,
  own: FieldNarrowingWire | undefined,
): string[] {
  const all = (field.options ?? []).map((o) => o.code);
  return own?.allowed_options?.length
    ? all.filter((c) => own.allowed_options!.includes(c))
    : all;
}

/**
 * *Edit options…* on a catalogue item (catalogue-management-in-one, rulings 2 and 15): over the parent's options,
 * which the dry run gives as `widget_context.parent`, each field's chips say what is offered and ★ marks the option
 * customers start on (the workbook's `*`). A field left with one option is fixed, and only then can it be highlighted;
 * ★ and the highlight tick stay in place, greyed, so nothing moves. Routes are *Set fulfilment routes…*'s, and the
 * action keeps them.
 */
function CatalogueItemNarrowing(props: CustomWidgetProps) {
  const { body, disabled, errors, hints, setValues } = props;
  const parent = hints[PATH]?.widget_context?.parent as
    ResolvedItemSchema | undefined;
  if (!parent?.item || !Array.isArray(parent.fields)) return null;
  const basedOn = hints[PATH]?.widget_context?.based_on as
    string | null | undefined;
  const space = resolvedItemFromSchema(parent);
  const narrowing = (body[PATH] as NarrowingWire | undefined) ?? {};
  const highlights = Array.isArray(body[HIGHLIGHTS])
    ? (body[HIGHLIGHTS] as string[])
    : [];
  const fields = space.fields.filter(isNarrowable);
  const allQuantities = space.quantityBreaks.map((b) => b.qty);
  const quantities = narrowing.quantities?.length
    ? narrowing.quantities
    : allQuantities;

  /** Write one field's offered options and start, keeping the rest; a highlight goes when its field is no longer fixed. */
  const write = (
    field: CatalogueField,
    offered: string[],
    start: string | null,
  ) => {
    const all = (field.options ?? []).map((o) => o.code);
    const own: FieldNarrowingWire = {
      allowed_options: offered.length === all.length ? [] : offered,
      // The start is kept while its option is offered, even alone, so it survives another option being offered again.
      ...(start && offered.includes(start) ? { default: start } : {}),
    };
    const nextFields = { ...narrowing.fields, [field.key]: own };
    const stillFixed = (key: string) => {
      const f = space.fields.find((x) => x.key === key);
      return f
        ? offeredOf(f, nextFields[key]).length === 1 ||
            (f.options?.length ?? 0) === 1
        : false;
    };
    setValues({
      [PATH]: { fields: nextFields, quantities: narrowing.quantities ?? [] },
      [HIGHLIGHTS]: highlights.filter(stillFixed),
    });
  };

  const setQuantities = (next: number[]) =>
    setValues({
      [PATH]: {
        fields: narrowing.fields ?? {},
        quantities: next.length === allQuantities.length ? [] : next,
      },
    });

  return (
    <fieldset disabled={disabled} className="space-y-3">
      {basedOn && (
        // What the options come from, so the person knows why these are the ones on offer.
        <p className="rounded-md bg-muted px-3 py-2 text-sm">
          Based on <span className="font-medium">{basedOn}</span>. This item can
          offer all of its options or fewer.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Click an option to offer it or stop offering it. ★ marks the option
        customers start on.
      </p>
      {fields.map((field) => {
        const own = narrowing.fields?.[field.key];
        const offered = offeredOf(field, own);
        // A fixed field starts on its one option, so offering another again keeps that start rather than losing it.
        const start =
          own?.default ?? (offered.length === 1 ? offered[0] : null);
        const fixed =
          offered.length === 1
            ? field.options?.find((o) => o.code === offered[0])
            : null;
        const highlightId = `highlight-${field.key}`;
        return (
          <div
            key={field.key}
            className="space-y-2 rounded-md border border-border p-3"
          >
            <div className="text-sm font-medium">{field.label}</div>
            <div className="flex flex-wrap gap-2">
              {(field.options ?? []).map((option) => {
                const isOffered = offered.includes(option.code);
                const isFixedHere = isOffered && offered.length === 1;
                // ★ stays on every chip so nothing moves: greyed where it cannot apply.
                const isStart = isFixedHere || start === option.code;
                return (
                  <span
                    key={option.code}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm ${
                      isOffered
                        ? "border-border"
                        : "border-border/50 text-muted-foreground"
                    }`}
                  >
                    <button
                      type="button"
                      className={isOffered ? "" : "line-through"}
                      title={
                        isOffered
                          ? `Stop offering ${option.label}`
                          : `Offer ${option.label}`
                      }
                      onClick={() =>
                        isOffered
                          ? offered.length > 1 &&
                            write(
                              field,
                              offered.filter((c) => c !== option.code),
                              start,
                            )
                          : write(field, [...offered, option.code], start)
                      }
                    >
                      {option.label}
                    </button>
                    <button
                      type="button"
                      disabled={!isOffered || isFixedHere}
                      aria-label={
                        isStart
                          ? `Customers start on ${option.label}`
                          : `Start customers on ${option.label}`
                      }
                      title={
                        isFixedHere
                          ? `${option.label} is the only option offered`
                          : isOffered
                            ? isStart
                              ? `Customers start on ${option.label}`
                              : `Start customers on ${option.label}`
                            : `Offer ${option.label} first`
                      }
                      onClick={() =>
                        write(
                          field,
                          offered,
                          start === option.code ? null : option.code,
                        )
                      }
                      className="disabled:cursor-default"
                    >
                      <Star
                        className={`size-3.5 ${
                          isStart
                            ? "fill-primary text-primary"
                            : isOffered
                              ? "text-muted-foreground"
                              : "text-muted-foreground/40"
                        }`}
                      />
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Checkbox
                id={highlightId}
                disabled={!fixed}
                checked={!!fixed && highlights.includes(field.key)}
                onCheckedChange={(on) =>
                  setValues({
                    [HIGHLIGHTS]:
                      on === true
                        ? [
                            ...highlights.filter((k) => k !== field.key),
                            field.key,
                          ]
                        : highlights.filter((k) => k !== field.key),
                  })
                }
              />
              <Label
                htmlFor={highlightId}
                className={`text-sm font-normal ${fixed ? "" : "text-muted-foreground"}`}
              >
                Highlight on the product page
                {/* Inside the label so it greys with it while the tick is disabled. */}
                {!fixed && <span>Available when one option is left</span>}
              </Label>
            </div>
          </div>
        );
      })}
      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="text-sm font-medium">Quantities offered</div>
        <div className="flex flex-wrap gap-2">
          {allQuantities.map((q) => {
            const isOffered = quantities.includes(q);
            return (
              <button
                key={q}
                type="button"
                className={`rounded-md border px-2 py-1 text-sm ${
                  isOffered
                    ? "border-border"
                    : "border-border/50 text-muted-foreground line-through"
                }`}
                onClick={() =>
                  isOffered
                    ? quantities.length > 1 &&
                      setQuantities(quantities.filter((x) => x !== q))
                    : setQuantities([...quantities, q].sort((a, b) => a - b))
                }
              >
                {q.toLocaleString("en-GB")}
              </button>
            );
          })}
        </div>
      </div>
      {errors.map((e) => (
        <p key={`${e.field}:${e.code}`} className="text-sm text-red-600">
          {e.message}
        </p>
      ))}
    </fieldset>
  );
}

export const catalogueItemNarrowingWidget: CustomWidget = {
  ownedPaths: [PATH, HIGHLIGHTS],
  Component: CatalogueItemNarrowing,
};
